import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Calendar,
  Clock,
  Plus,
  Trash2,
  Play,
  Pause,
  MoreVertical,
  Sun,
  Cloud,
  CalendarDays,
  Timer,
  CheckCircle,
  XCircle,
  Zap,
  Copy,
} from 'lucide-react';

const RULE_TYPES = [
  { value: 'time_range', label: 'Time Range', icon: Clock, description: 'Play during specific hours' },
  { value: 'day_of_week', label: 'Day of Week', icon: CalendarDays, description: 'Play on specific days' },
  { value: 'date_range', label: 'Date Range', icon: Calendar, description: 'Play during a date period' },
  { value: 'blackout', label: 'Blackout Dates', icon: XCircle, description: 'Exclude specific dates' },
  { value: 'weather', label: 'Weather Trigger', icon: Cloud, description: 'Play based on weather' },
  { value: 'event', label: 'Event Trigger', icon: Zap, description: 'Play based on events' },
];

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function AdvancedSchedulingPage() {
  const { user, isAdmin, isOperator } = useAuth();
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [content, setContent] = useState([]);
  const [screens, setScreens] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    content_id: '',
    screen_ids: [],
    rules: [],
    fallback_content_id: '',
    is_active: true,
  });
  const [currentRule, setCurrentRule] = useState({
    rule_type: 'time_range',
    enabled: true,
    priority: 0,
    conditions: {},
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [schedulesRes, templatesRes, contentRes, screensRes] = await Promise.all([
        api.get('/schedules/advanced'),
        api.get('/schedules/rule-templates'),
        api.get('/content'),
        api.get('/screens'),
      ]);
      setSchedules(schedulesRes.data);
      setTemplates(templatesRes.data.templates || []);
      setContent(contentRes.data.filter(c => c.approval_status === 'approved'));
      setScreens(screensRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = () => {
    if (!currentRule.rule_type) return;

    // Set default conditions based on rule type
    let conditions = {};
    switch (currentRule.rule_type) {
      case 'time_range':
        conditions = { start_hour: 9, end_hour: 17 };
        break;
      case 'day_of_week':
        conditions = { days: [0, 1, 2, 3, 4] };
        break;
      case 'date_range':
        conditions = {
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        };
        break;
      case 'blackout':
        conditions = { dates: [] };
        break;
      case 'weather':
        conditions = { condition: 'sunny', temperature_min: 65 };
        break;
      case 'event':
        conditions = { event_type: 'sports', trigger: 'game_day' };
        break;
    }

    setFormData({
      ...formData,
      rules: [...formData.rules, { ...currentRule, conditions }],
    });
    setCurrentRule({
      rule_type: 'time_range',
      enabled: true,
      priority: 0,
      conditions: {},
    });
  };

  const handleRemoveRule = (index) => {
    setFormData({
      ...formData,
      rules: formData.rules.filter((_, i) => i !== index),
    });
  };

  const handleUseTemplate = (template) => {
    setFormData({
      ...formData,
      name: template.name,
      rules: template.rules,
    });
    setTemplateDialogOpen(false);
    toast.success(`Template "${template.name}" applied`);
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    if (formData.rules.length === 0) {
      toast.error('Please add at least one scheduling rule');
      return;
    }
    try {
      await api.post('/schedules/advanced', formData);
      toast.success('Schedule created successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Failed to create schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    try {
      await api.delete(`/schedules/advanced/${scheduleId}`);
      toast.success('Schedule deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleEvaluateSchedule = async (scheduleId) => {
    try {
      const response = await api.get(`/schedules/advanced/${scheduleId}/evaluate`);
      setEvaluationResult(response.data);
    } catch (error) {
      toast.error('Failed to evaluate schedule');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      content_id: '',
      screen_ids: [],
      rules: [],
      fallback_content_id: '',
      is_active: true,
    });
  };

  const updateRuleCondition = (ruleIndex, key, value) => {
    const updatedRules = [...formData.rules];
    updatedRules[ruleIndex].conditions[key] = value;
    setFormData({ ...formData, rules: updatedRules });
  };

  const toggleDay = (ruleIndex, day) => {
    const updatedRules = [...formData.rules];
    const days = updatedRules[ruleIndex].conditions.days || [];
    if (days.includes(day)) {
      updatedRules[ruleIndex].conditions.days = days.filter(d => d !== day);
    } else {
      updatedRules[ruleIndex].conditions.days = [...days, day].sort();
    }
    setFormData({ ...formData, rules: updatedRules });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="advanced-scheduling-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Advanced Scheduling
          </h1>
          <p className="text-zinc-400 mt-1">
            Create conditional content schedules with multiple rules
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setTemplateDialogOpen(true)}
            className="border-zinc-700"
          >
            <Copy className="w-4 h-4 mr-2" />
            Use Template
          </Button>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="create-schedule-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Schedule
          </Button>
        </div>
      </div>

      {/* Evaluation Result */}
      {evaluationResult && (
        <Card className="glass-card border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-white">
              Schedule Evaluation: {evaluationResult.schedule_name}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setEvaluationResult(null)}>
              <XCircle className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4">
              {evaluationResult.should_play ? (
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Should Play
                </Badge>
              ) : (
                <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                  <XCircle className="w-4 h-4 mr-1" />
                  Should Not Play
                </Badge>
              )}
              <span className="text-zinc-400 text-sm">
                Evaluated at {new Date(evaluationResult.evaluated_at).toLocaleString()}
              </span>
            </div>
            <div className="space-y-2">
              {evaluationResult.rules_evaluation?.map((rule, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg flex items-center justify-between ${
                    rule.passed ? 'bg-emerald-500/10' : 'bg-red-500/10'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {rule.passed ? (
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className="text-white capitalize">{rule.rule_type.replace('_', ' ')}</span>
                  </div>
                  <span className="text-zinc-400 text-sm">{rule.reason}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedules List */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white">Active Schedules</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {schedules.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-semibold text-white mb-2">No advanced schedules</h3>
              <p className="text-zinc-400 mb-4">Create your first conditional schedule</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Name</TableHead>
                  <TableHead className="text-zinc-500">Content</TableHead>
                  <TableHead className="text-zinc-500">Rules</TableHead>
                  <TableHead className="text-zinc-500">Screens</TableHead>
                  <TableHead className="text-zinc-500">Status</TableHead>
                  <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow key={schedule.id} className="border-zinc-800">
                    <TableCell className="text-white font-medium">{schedule.name}</TableCell>
                    <TableCell className="text-zinc-400">{schedule.content_name}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {schedule.rules?.slice(0, 3).map((rule, i) => (
                          <Badge key={i} variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs">
                            {rule.rule_type.replace('_', ' ')}
                          </Badge>
                        ))}
                        {schedule.rules?.length > 3 && (
                          <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs">
                            +{schedule.rules.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-400">
                      {schedule.screen_ids?.length || 0} screens
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        schedule.is_active
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                          : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                      }>
                        {schedule.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                          <DropdownMenuItem
                            onClick={() => handleEvaluateSchedule(schedule.id)}
                            className="cursor-pointer"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Evaluate Now
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteSchedule(schedule.id)}
                            className="cursor-pointer text-red-400"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Schedule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Create Advanced Schedule
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSchedule} className="space-y-4">
            <div>
              <Label className="text-zinc-300">Schedule Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Morning Campaign"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Content</Label>
                <Select
                  value={formData.content_id}
                  onValueChange={(value) => setFormData({ ...formData, content_id: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue placeholder="Select content" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {content.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-zinc-300">Fallback Content</Label>
                <Select
                  value={formData.fallback_content_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, fallback_content_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue placeholder="Select fallback" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="none">None</SelectItem>
                    {content.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Rules Section */}
            <div className="space-y-3">
              <Label className="text-zinc-300">Scheduling Rules</Label>
              
              {/* Existing Rules */}
              {formData.rules.map((rule, index) => (
                <div key={index} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                      {rule.rule_type.replace('_', ' ')}
                    </Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveRule(index)}
                      className="text-red-400 h-6"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  {/* Rule-specific conditions */}
                  {rule.rule_type === 'time_range' && (
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Label className="text-xs text-zinc-500">Start Hour</Label>
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={rule.conditions.start_hour}
                          onChange={(e) => updateRuleCondition(index, 'start_hour', parseInt(e.target.value))}
                          className="bg-zinc-900 border-zinc-700 text-white mt-1 h-8"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-zinc-500">End Hour</Label>
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={rule.conditions.end_hour}
                          onChange={(e) => updateRuleCondition(index, 'end_hour', parseInt(e.target.value))}
                          className="bg-zinc-900 border-zinc-700 text-white mt-1 h-8"
                        />
                      </div>
                    </div>
                  )}

                  {rule.rule_type === 'day_of_week' && (
                    <div className="flex gap-1 flex-wrap">
                      {DAY_NAMES.map((day, dayIndex) => (
                        <Button
                          key={day}
                          type="button"
                          size="sm"
                          variant={rule.conditions.days?.includes(dayIndex) ? 'default' : 'outline'}
                          onClick={() => toggleDay(index, dayIndex)}
                          className={`h-8 ${rule.conditions.days?.includes(dayIndex) ? 'bg-blue-600' : 'border-zinc-700'}`}
                        >
                          {day}
                        </Button>
                      ))}
                    </div>
                  )}

                  {rule.rule_type === 'date_range' && (
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Label className="text-xs text-zinc-500">Start Date</Label>
                        <Input
                          type="date"
                          value={rule.conditions.start_date}
                          onChange={(e) => updateRuleCondition(index, 'start_date', e.target.value)}
                          className="bg-zinc-900 border-zinc-700 text-white mt-1 h-8"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-zinc-500">End Date</Label>
                        <Input
                          type="date"
                          value={rule.conditions.end_date}
                          onChange={(e) => updateRuleCondition(index, 'end_date', e.target.value)}
                          className="bg-zinc-900 border-zinc-700 text-white mt-1 h-8"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Add Rule */}
              <div className="flex gap-2">
                <Select
                  value={currentRule.rule_type}
                  onValueChange={(value) => setCurrentRule({ ...currentRule, rule_type: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white flex-1">
                    <SelectValue placeholder="Select rule type" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {RULE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={handleAddRule}
                  variant="outline"
                  className="border-zinc-700"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label className="text-zinc-300">Active</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-500">
                Create Schedule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Schedule Templates
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {templates.map((template, index) => (
              <div
                key={index}
                className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 hover:border-blue-500/50 cursor-pointer transition-colors"
                onClick={() => handleUseTemplate(template)}
              >
                <h4 className="text-white font-medium">{template.name}</h4>
                <p className="text-zinc-400 text-sm mt-1">{template.description}</p>
                <div className="flex gap-1 mt-2 flex-wrap">
                  {template.rules.map((rule, i) => (
                    <Badge key={i} variant="outline" className="bg-zinc-900 text-zinc-400 border-zinc-700 text-xs">
                      {rule.rule_type.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
